import React, { useMemo } from 'react';

export interface MarkBreakup {
  marks: string;
  count: string;
}

interface MarkBreakupInputProps {
  value: MarkBreakup[];
  onChange: (value: MarkBreakup[]) => void;
  totalMarks: number;
}

export const MarkBreakupInput: React.FC<MarkBreakupInputProps> = ({ value, onChange, totalMarks }) => {
  const calculatedTotal = useMemo(() => {
    return value.reduce((acc, item) => {
      const marks = parseInt(item.marks, 10) || 0;
      const count = parseInt(item.count, 10) || 0;
      return acc + (marks * count);
    }, 0);
  }, [value]);

  const handleItemChange = (index: number, field: keyof MarkBreakup, fieldValue: string) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], [field]: fieldValue };
    onChange(newValue);
  };

  const handleAddItem = () => {
    onChange([...value, { marks: '', count: '' }]);
  };

  const handleRemoveItem = (index: number) => {
    if (value.length > 1) {
      onChange(value.filter((_, i) => i !== index));
    }
  };

  const isTotalMismatch = calculatedTotal !== totalMarks;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <label className="block text-sm font-bold text-brand-grey">
          Question Mark Breakup
        </label>
        <span className={`text-sm font-semibold ${isTotalMismatch ? 'text-red-600' : 'text-green-600'}`}>
          Calculated: {calculatedTotal} / {totalMarks}
        </span>
      </div>
      <div className="space-y-2 p-3 border border-gray-300 rounded-md bg-gray-50">
        <div className="flex items-center space-x-2 text-xs font-semibold text-gray-500 px-1 mb-1">
          <div className="flex-1">Marks</div>
          <div className="w-5 flex-shrink-0"></div> {/* Spacer for the 'x' */}
          <div className="flex-1">No. of Questions</div>
          <div className="w-9 flex-shrink-0"></div> {/* Spacer for the button */}
        </div>

        {value.map((item, index) => (
          <div key={index} className="flex items-center space-x-2">
            <div className="flex-1">
              <input
                type="number"
                placeholder="e.g., 5"
                value={item.marks}
                onChange={(e) => handleItemChange(index, 'marks', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm bg-white text-brand-grey"
                aria-label={`Marks for breakup item ${index + 1}`}
              />
            </div>
            <span className="w-5 text-center text-gray-500 flex-shrink-0">×</span>
            <div className="flex-1">
              <input
                type="number"
                placeholder="e.g., 10"
                value={item.count}
                onChange={(e) => handleItemChange(index, 'count', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm bg-white text-brand-grey"
                aria-label={`Number of questions for breakup item ${index + 1}`}
              />
            </div>
            <button
              onClick={() => handleRemoveItem(index)}
              disabled={value.length <= 1}
              className="p-2 w-9 h-9 flex-shrink-0 flex items-center justify-center text-gray-500 rounded-full hover:bg-red-100 hover:text-red-600 disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              aria-label={`Remove breakup item ${index + 1}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={handleAddItem}
        className="mt-2 w-full text-sm font-semibold text-brand-orange hover:text-opacity-80 transition-colors"
      >
        + Add another breakup
      </button>
    </div>
  );
};